import { LoanStatusExternal, MerchantStructure, MetricAgentView } from '@nemuru-eng/js-api-client';

import MerchantStructureEntity from '@/entities/MerchantStructureEntity';

import { CustomDate } from '../../shared/entities/CustomDate';
import { MetricAnalyzer } from '../../shared/entities/MetricAnalyzer';
import { TimeInterval } from '../../shared/entities/TimeInterval';
import { ScoreCardMetricKey } from '../../shared/types/ScoreCardMetricKey';
import { AgentScoreCardMetrics, ChannelScoreCardMetrics } from '../types/AgentScoreCardMetrics';
export class AgentMetricAnalyzer {
  constructor(public readonly timeInterval: TimeInterval, public readonly metricViews?: MetricAgentView[]) {}

  private sortArray(
    accumulatedMetricsArray: ChannelScoreCardMetrics[] | AgentScoreCardMetrics[],
    selectedMetricsType: ScoreCardMetricKey,
  ): ChannelScoreCardMetrics[] | AgentScoreCardMetrics[] {
    return accumulatedMetricsArray.sort((a, b) =>
      b[selectedMetricsType].current > a[selectedMetricsType].current ? 1 : -1,
    );
  }

  private metricsCalculation(
    loanStatusExternal: LoanStatusExternal,
    structure: MerchantStructure,
  ): AgentScoreCardMetrics[] {
    if (!this.metricViews) return [];
    const currentIntervalEndDate = new CustomDate().date;
    const currentIntervalStartDate = new CustomDate().subtractDays(this.timeInterval.days).date;
    const previousIntervalStartDate = new CustomDate().subtractDays(this.timeInterval.days * 2).date;

    const filteredMetricViews = this.metricViews.map((metricView) => {
      return {
        ...metricView,
        metrics: metricView.metrics.filter((metric) => metric.loanStatusExternal === loanStatusExternal),
      };
    });

    const scoreCardMetrics: AgentScoreCardMetrics[] = [];

    filteredMetricViews.forEach((metricView) => {
      const metricsCalculationResult = new MetricAnalyzer(metricView).metricAnalyzerBaseMetrics(
        currentIntervalEndDate,
        currentIntervalStartDate,
        previousIntervalStartDate,
      );

      const merchantStructureEntity = MerchantStructureEntity.create({
        merchantStructure: structure,
        agentId: metricView.agentId,
      });
      const channel = merchantStructureEntity.getChannel();
      const agent = merchantStructureEntity.getAgent();

      scoreCardMetrics.push({
        merchantId: metricView.merchantId,
        agentId: metricView.agentId,
        agentName: metricView.agentName,
        isAgentBlocked: agent?.isBlocked,
        channelId: metricView.channelId,
        channelDescription: channel?.description ?? '',
        channelType: channel?.type,
        ...metricsCalculationResult,
      } as AgentScoreCardMetrics);
    });

    return scoreCardMetrics;
  }

  public channelMetrics(
    loanStatusExternal: LoanStatusExternal,
    structure: MerchantStructure,
    selectedMetricsType: ScoreCardMetricKey,
  ): ChannelScoreCardMetrics[] {
    const channelMetrics = this.metricsCalculation(loanStatusExternal, structure);
    const accumulatedArray: ChannelScoreCardMetrics[] = [];

    for (const obj of channelMetrics) {
      const key = obj.channelId;

      if (!accumulatedArray.some((element) => element.channelId === key)) {
        accumulatedArray.push(obj);
      } else {
        const channelValues = accumulatedArray.find((element) => element.channelId === key);
        if (channelValues) {
          channelValues.totalPrincipalAmount.current += obj.totalPrincipalAmount.current;
          channelValues.totalPrincipalAmount.previous += obj.totalPrincipalAmount.previous;
          channelValues.totalLoansNumber.current += obj.totalLoansNumber.current;
          channelValues.totalLoansNumber.previous += obj.totalLoansNumber.previous;
        }
      }
    }

    accumulatedArray.forEach((object) => {
      (object.totalLoansNumber.variation =
        object.totalLoansNumber.previous === 0
          ? undefined
          : (object.totalLoansNumber.current - object.totalLoansNumber.previous) / object.totalLoansNumber.previous),
        (object.totalPrincipalAmount.variation =
          object.totalPrincipalAmount.previous === 0
            ? undefined
            : (object.totalPrincipalAmount.current - object.totalPrincipalAmount.previous) /
              object.totalPrincipalAmount.previous);
    });

    return this.sortArray(accumulatedArray, selectedMetricsType);
  }

  public agentMetrics(
    loanStatusExternal: LoanStatusExternal,
    structure: MerchantStructure,
    selectedMetricsType: ScoreCardMetricKey,
  ): AgentScoreCardMetrics[] | ChannelScoreCardMetrics[] {
    const scoreCardMetrics = this.metricsCalculation(loanStatusExternal, structure);
    return this.sortArray(scoreCardMetrics, selectedMetricsType);
  }
}
